import React from "react";
import { divisionLabel } from "@/lib/divisions";

/* Division glyphs (Phase 1b polish) — monochrome hints, currentColor.
   Drilling = the PDC bit face RockLink already uses (pdc-bit-small, FILLED —
   dense ink). Metallic = machined hex with bore (STROKED — light ink).
   The fill-vs-stroke weight difference keeps them distinct at 10–12px. */

export function MetallicGlyph({ size = 12, strokeWidth = 2.2, className = "", ...rest }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth={strokeWidth} strokeLinejoin="round" className={className} aria-hidden="true" {...rest}>
      <path d="M12 2.5l7.8 4.5v9l-7.8 4.5-7.8-4.5v-9z" />
      <circle cx="12" cy="11.75" r="3.2" />
    </svg>
  );
}

/* "All divisions" mark — three overlapping tiles (a stack), stroked to match
   the metallic hex weight so the trio reads as one family. */
export function AllDivisionsGlyph({ size = 12, strokeWidth = 2, className = "", ...rest }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth={strokeWidth} strokeLinejoin="round" className={className} aria-hidden="true" {...rest}>
      <rect x="3.5" y="3.5" width="11" height="11" rx="2.5" />
      <path d="M9.5 17.2v1.3a2 2 0 0 0 2 2h7a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2h-1.3" />
    </svg>
  );
}

export function DrillingGlyph({ size = 12, className = "", ...rest }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true" {...rest}>
      <path fillRule="evenodd" d="M 10.27 9.43 Q 9.55 6.97 12.68 2.22 A 9.8 9.8 0 0 1 20.65 7.40 Q 17.66 7.89 12.00 8.70 A 3.2 3.2 0 0 0 10.27 9.43 Z M 13.91 9.56 Q 16.03 8.11 21.51 9.63 A 9.8 9.8 0 0 1 19.05 18.81 Q 17.66 16.11 15.14 10.98 A 3.2 3.2 0 0 0 13.91 9.56 Z M 14.91 13.06 Q 16.94 14.63 17.19 20.31 A 9.8 9.8 0 0 1 7.70 20.81 Q 9.84 18.66 13.94 14.67 A 3.2 3.2 0 0 0 14.91 13.06 Z M 11.89 15.10 Q 11.03 17.51 5.70 19.51 A 9.8 9.8 0 0 1 2.30 10.64 Q 5.00 12.00 10.06 14.67 A 3.2 3.2 0 0 0 11.89 15.10 Z M 9.02 12.85 Q 6.45 12.78 2.91 8.33 A 9.8 9.8 0 0 1 10.30 2.35 Q 9.84 5.34 8.86 10.98 A 3.2 3.2 0 0 0 9.02 12.85 Z M 13.50 12.00 A 1.50 1.50 0 1 0 10.50 12.00 A 1.50 1.50 0 1 0 13.50 12.00 Z" />
    </svg>
  );
}

/* Convenience wrapper: picks the right glyph for a division key.
   Ecotile has no pipeline glyph (own module) — renders nothing. */
export default function DivisionGlyph({ division, size = 12, withTitle = true, className = "", ...rest }) {
  const common = withTitle ? { ...rest } : rest;
  if (division === "drilling") {
    return (
      <span title={withTitle ? `${divisionLabel(division)} division` : undefined} className="inline-flex items-center shrink-0 [&>svg]:block">
        <DrillingGlyph size={size} className={className} {...common} />
      </span>
    );
  }
  if (division === "metallic") {
    return (
      <span title={withTitle ? `${divisionLabel(division)} division` : undefined} className="inline-flex items-center shrink-0 [&>svg]:block">
        <MetallicGlyph size={size} className={className} {...common} />
      </span>
    );
  }
  return null;
}
